import EventEmitter from 'events';
import { TextToSpeech } from './textToSpeech';
import { getUserAccess, isMasterAdmin } from '../config/accessControl';

class VoiceBridge extends EventEmitter {
    constructor(userEmail, initialMode = 'text') {
        super();
        this.ws = null;
        this.isRunning = false;
        this.stream = null;
        this.audioContext = null;
        this.source = null;
        this.processor = null;
        this.isConnecting = false;
        this.wsReady = false;
        this.userEmail = userEmail;
        this.currentMode = initialMode;
        this.lastAudioProcessTime = Date.now();
        this.checkAudioInterval = null;
        
        this.CHUNK_SIZE = 512;
        this.SAMPLE_RATE = 16000;
        this.CHANNELS = 1;
        
        if (!process.env.REACT_APP_STT_ENDPOINT) {
            throw new Error('WebSocket endpoint not configured (REACT_APP_STT_ENDPOINT)');
        }
        this.baseWsUrl = process.env.REACT_APP_STT_ENDPOINT;
        
        this.tts = new TextToSpeech();
        this.currentReader = null;
        this.isProcessingResponse = false;
        this.messageBuffer = [];
        this.lastMessageTime = null;
        this.CONSECUTIVE_MESSAGE_THRESHOLD = 5000; // 5 seconds
        this.messageQueue = [];
        this.isProcessing = false;
        
        // Update WS_URL based on mode
        this.getWsUrl = (mode) => {
            const baseUrl = this.baseWsUrl;
            return mode === 'mixed' || mode === 'full-voice' 
                ? `${baseUrl}/ws/transcribe?user_id=${encodeURIComponent(this.userEmail)}`
                : `${baseUrl}/ws?user_id=${encodeURIComponent(this.userEmail)}`;
        };
    }

    async start(mode = 'text') {
        try {
            // Check if user has access to this mode
            const userAccess = await getUserAccess(this.userEmail);
            
            // Master admin or admin users get all modes
            if (isMasterAdmin(this.userEmail) || userAccess?.level === 'admin') {
                console.log('Admin user - all modes allowed');
            } else {
                // For partners, check specific allowed modes
                const allowedModes = userAccess?.modes || ['text'];
                if (!allowedModes.includes(mode)) {
                    console.log(`User does not have access to ${mode} mode, defaulting to text mode`);
                    this.emit('status', `You don't have access to ${mode} mode. Using text mode.`);
                    mode = 'text';
                }
            }

            if (mode === 'text') {
                console.log('Text mode - voice recognition disabled');
                this.cleanup();
                return;
            }

            if (this.isRunning || this.isConnecting) return;

            this.isConnecting = true;
            this.currentMode = mode;
            
            // Use the correct WebSocket URL based on mode
            this.ws = new WebSocket(this.getWsUrl(mode));
            
            this.ws.onopen = () => {
                this.wsReady = true;
                this.emit('status', 'Connected to voice server');
            };

            this.ws.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                switch (data.type) {
                    case 'transcription':
                        if (data.text?.trim()) {
                            if (this.tts) {
                                await this.tts.stopCurrent();
                            }
                            this.emit('transcription', data.text.trim());
                            
                            // Add to queue instead of processing immediately
                            this.messageQueue.push(data.text.trim());
                            this.processMessageQueue();
                        }
                        break;
                    case 'api_response':
                        if (data.text?.trim()) {
                            if (!this.mixedModeBuffer) this.mixedModeBuffer = '';
                            this.mixedModeBuffer += data.text;
                            
                            // Look for sentence boundaries
                            const matches = [...this.mixedModeBuffer.matchAll(/[.!?]\s+/g)];
                            if (matches.length > 0) {
                                const lastMatch = matches[matches.length - 1];
                                const completeText = this.mixedModeBuffer.substring(0, lastMatch.index + 2); // Include the space after punctuation
                                
                                if (completeText.trim()) {
                                    // Ensure proper spacing between sentences
                                    this.emit('apiResponse', completeText);
                                    
                                    // Send to TTS with previous sentence as context
                                    if (this.tts && this.currentMode === 'full-voice') {
                                        const context = this.previousSentence ? this.previousSentence + " " : "";
                                        await this.tts.synthesize(context + completeText);
                                        this.previousSentence = completeText;
                                    }
                                }
                                
                                this.mixedModeBuffer = this.mixedModeBuffer.substring(lastMatch.index + 2);
                            }
                        }
                        break;
                    case 'status':
                        this.emit('status', data.message);
                        break;
                    case 'error':
                        this.emit('error', data);
                        break;
                }
            };

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: this.SAMPLE_RATE,
                    channelCount: this.CHANNELS,
                    // Simplified audio constraints
                    latency: 0,
                    noiseSuppression: false,
                    echoCancellation: false
                }
            });

            // Create audio context first
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: this.SAMPLE_RATE,
                latencyHint: 'interactive'
            });

            // Resume audio context (needed for Chrome)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            this.stream = stream;
            this.source = this.audioContext.createMediaStreamSource(stream);
            this.processor = this.audioContext.createScriptProcessor(this.CHUNK_SIZE, 1, 1);

            this.processor.onaudioprocess = (e) => {
                this.lastAudioProcessTime = Date.now();
                if (this.wsReady && this.ws?.readyState === WebSocket.OPEN) {
                    const inputData = e.inputBuffer.getChannelData(0);
                    const amplitude = this.getAverageAmplitude(inputData);
                    
                    if (amplitude > 0.0001) {
                        try {
                            this.ws.send(inputData.buffer);
                        } catch (error) {
                            console.error('Error sending audio chunk:', error);
                            this.wsReady = false;
                        }
                    }
                }
            };

            this.source.connect(this.processor);
            this.processor.connect(this.audioContext.destination);

            this.isRunning = true;
            this.checkAudioInterval = setInterval(() => this.checkAudioProcessing(), 5000);

        } catch (error) {
            console.error('Error starting voice bridge:', error);
            this.emit('error', { 
                message: "Audio setup failed. Please try again or use text mode."
            });
            this.cleanup();
        } finally {
            this.isConnecting = false;
        }
    }

    async processMessageQueue() {
        if (this.isProcessing || this.messageQueue.length === 0) return;
        
        this.isProcessing = true;
        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            try {
                if (this.currentMode === 'full-voice') {
                    await this.processMessageBuffer(message);
                } else if (this.currentMode === 'mixed') {
                    await this.handleMixedModeResponse(message);
                }
            } catch (error) {
                console.error('Error processing message:', error);
            }
        }
        this.isProcessing = false;
    }

    async processMessageBuffer(newMessage) {
        const currentTime = Date.now();
        
        if (this.lastMessageTime && (currentTime - this.lastMessageTime) > this.CONSECUTIVE_MESSAGE_THRESHOLD) {
            this.messageBuffer = [];
        }
        
        this.lastMessageTime = currentTime;
        this.messageBuffer.push(newMessage);

        // Stop any current TTS
        if (this.tts) {
            await this.tts.stopCurrent();
        }

        // If this is a consecutive message, cancel any ongoing response
        if (this.messageBuffer.length > 1) {
            if (this.currentReader) {
                this.currentReader.cancel();
            }
            // Process only the latest message
            const latestMessage = this.messageBuffer.pop();
            this.messageBuffer = [];
            await this.handleFullVoiceResponse(latestMessage);
        } else {
            await this.handleFullVoiceResponse(newMessage);
        }
    }

    async handleFullVoiceResponse(text) {
        try {
            if (this.tts) {
                await this.tts.stopCurrent();
            }

            const API_URL = process.env.REACT_APP_AI_ENDPOINT_URL;
            const response = await fetch(`${API_URL}/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: text,
                    user_id: this.userEmail,
                    session_id: this.sessionId
                })
            });

            if (!response.ok) {
                throw new Error(`API returned status ${response.status}`);
            }

            const reader = response.body.getReader();
            this.currentReader = reader;
            const decoder = new TextDecoder();
            let fullResponse = '';
            let lastEmittedLength = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value);
                fullResponse += chunk;
                
                // Emit only the new content without any truncation
                const newContent = fullResponse.slice(lastEmittedLength);
                if (newContent) {
                    this.emit('apiResponse', newContent);
                    lastEmittedLength = fullResponse.length;
                }
            }

            if (fullResponse && this.tts) {
                await this.tts.speak(fullResponse);
            }

            return fullResponse;

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error in full-voice mode:', error);
                this.emit('error', { message: error.message });
            }
        } finally {
            this.currentReader = null;
        }
    }

    async handleMixedModeResponse(text) {
        try {
            const API_URL = process.env.REACT_APP_AI_ENDPOINT_URL;
            const response = await fetch(`${API_URL}/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: text,
                    user_id: this.userEmail,
                    session_id: this.sessionId
                })
            });

            if (!response.ok) {
                throw new Error(`API returned status ${response.status}`);
            }

            const reader = response.body.getReader();
            this.currentReader = reader;
            const decoder = new TextDecoder();
            
            // Don't accumulate - just pass through each chunk
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value);
                // Emit just this chunk
                this.emit('apiResponse', chunk);
            }

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error in mixed mode:', error);
                this.emit('error', { message: error.message });
            }
        } finally {
            this.currentReader = null;
        }
    }

    cleanup() {
        if (this.checkAudioInterval) {
            clearInterval(this.checkAudioInterval);
            this.checkAudioInterval = null;
        }

        if (this.processor) {
            this.processor.disconnect();
            this.processor = null;
        }
        if (this.source) {
            this.source.disconnect();
            this.source = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.isRunning = false;
        this.isConnecting = false;
        this.wsReady = false;

        if (this.currentReader) {
            this.currentReader.cancel();
            this.currentReader = null;
        }
        if (this.tts) {
            this.tts.stopCurrent();
        }
        this.mixedModeBuffer = '';  // Clear the mixed mode buffer
    }

    stop() {
        this.cleanup();
    }

    checkAudioProcessing() {
        const now = Date.now();
        if (this.isRunning && (now - this.lastAudioProcessTime) > 5000) {
            console.log('No audio processed for 5 seconds, attempting reconnection...');
            this.cleanup();
            this.start();
        }
    }

    getAverageAmplitude(data) {
        return data.reduce((sum, value) => sum + Math.abs(value), 0) / data.length;
    }

    async sendMessage(message) {
        this.messageQueue.push(message);
        this.processMessageQueue();
    }
}

export default VoiceBridge;